<template>
  <div
    class="relative rounded"
    :class="errors.length > 0 ? 'shadow-outline-error' : ''"
  >
    <mia-multi-select
      v-bind="attrs"
      :options="availableCustomers"
      :class="{ 'is-required': required }"
      placeholder="Kunden durchsuchen"
      v-on="listeners"
    >
      <template
        v-if="!hasCustomCustomers"
        #noOptions
      >
        Bitte geben Sie einen Suchbegriff ein
      </template>
      <template
        v-if="!hasCustomCustomers"
        #noResults
      >
        <div class="flex items-center justify-between">
          <span>Kunden werden durchsucht...</span>
          <mia-spinner class="w-5 h-5 text-orange-mia" />
        </div>
      </template>
      <template #singleLabel="{ option }">
        <span
          v-if="hasName(option)"
          class="mr-1"
          v-text="getFullName(option)"
        />
        <span
          v-if="option.item_company !== ''"
          class="mr-1"
          v-text="`(${option.item_company})`"
        />
        <span
          v-if="hasAddress(option)"
          class="mr-1"
          v-text="`aus ${getAddress(option)}`"
        />
      </template>
      <template #option="{ option }">
        <div
          class="p-2 group border-b border-gray-400"
          style="margin: -12px;"
        >
          <div
            v-if="hasName(option)"
            class="text-lg text-gray-900 group-hover:text-white"
          >
            {{ getFullName(option) }}
          </div>
          <div
            v-if="hasAddress(option)"
            class="mt-1 text-gray-900 group-hover:text-white"
          >
            {{ getAddress(option) }}
          </div>
          <div
            v-if="option.item_company !== ''"
            class="text-sm tracking-wider text-gray-800 group-hover:text-gray-100"
          >
            {{ option.item_company }}
          </div>
          <div class="mt-2 text-sm flex items-center justify-between italic">
            <div>
              ID {{ option.id }}
              <span class="mx-2">•</span>
              9 Fzg.
            </div>
            <div>{{ option.item_dealer_name }}</div>
          </div>
        </div>
      </template>
    </mia-multi-select>

    <mia-fade-transition>
      <small
        v-if="errors.length > 0"
        class="absolute bottom-0 block leading-3 text-red-600 pointer-events-none"
        style="transform: translateY(17px)"
      >
        {{ errors[0] }}
      </small>
    </mia-fade-transition>
  </div>
</template>

<script>
import Fuse from 'fuse.js'
import debounce from 'lodash/debounce'
import { search } from '@/api/customer'
import Spinner from '@/components/ui/loader/Spinner.vue'

export default {
  components: {
    'mia-spinner': Spinner
  },

  props: {
    value: {
      type: [String, Number, Array, Object],
      required: true
    },

    customers: {
      type: Array,
      default() {
        return []
      }
    },

    required: {
      type: Boolean,
      default: false
    },

    errors: {
      type: Array,
      default() {
        return []
      }
    }
  },

  data() {
    return {
      options: [],
      query: '',
      fetchedCustomers: []
    }
  },

  computed: {
    attrs() {
      return {
        ...this.$attrs,
        value: this.value,
        'internal-search': false
      }
    },

    listeners() {
      return {
        ...this.$listeners,
        input: value => this.$emit('input', value),
        'search-change': query => this.onSearchChange(query)
      }
    },

    filteredOptions() {
      if (this.query === '') {
        return this.options
      }

      return this.fuse.search(this.query)
    },

    hasCustomCustomers() {
      return this.customers.length > 0
    },

    availableCustomers() {
      return this.hasCustomCustomers ? this.filteredOptions : this.fetchedCustomers
    }
  },

  watch: {
    customers: {
      immediate: true,
      handler(customers) {
        this.options = customers

        this.createFuse()
      }
    }
  },

  methods: {
    createFuse() {
      this.fuse = new Fuse(this.options, {
        isCaseSensitive: true,
        findAllMatches: true,
        threshold: 0.3,
        keys: [
          'item_firstname',
          'item_name',
          'item_company',
          'item_zip',
          'item_city'
        ]
      })
    },

    hasName(option) {
      return option.item_firstname !== '' || option.item_name !== ''
    },

    getFullName(option) {
      const separator = option.item_name !== '' && option.item_firstname !== '' ? ',' : ''

      return `${option.item_name}${separator} ${option.item_firstname}`.trim()
    },

    hasAddress(option) {
      return option.item_zip !== '' || option.item_city !== ''
    },

    getAddress(option) {
      return `${option.item_zip} ${option.item_city}`.trim()
    },

    // eslint-disable-next-line prefer-arrow-callback
    searchCustomers: debounce(async function (query) {
      this.fetchedCustomers = await search(query)
    }, 750),

    onSearchChange(query) {
      if (this.hasCustomCustomers) {
        this.query = query
      } else if (query.length > 0) {
        this.searchCustomers(query)
      } else {
        this.fetchedCustomers = []
      }

      if (this.$listeners['search-change']) {
        this.$listeners['search-change'](query)
      }
    }
  }
}
</script>

<style scoped>
.is-required ::v-deep .multiselect__tags {
  border-left-width: calc(theme('width.px') * 3);
  border-left-color: theme('borderColor.orange-mia');
}
</style>
